import {
  CONSENT_COOKIE_NAME,
  CONSENT_MAX_AGE_DAYS,
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION,
  DEFAULT_CATEGORIES,
  type ConsentCategories,
  type ConsentState,
} from "./types";

const isBrowser = () => typeof window !== "undefined";

function setCookie(name: string, value: string, days: number) {
  if (!isBrowser()) return;
  const maxAge = days * 24 * 60 * 60;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
}

function deleteCookie(name: string, paths: string[] = ["/"]) {
  if (!isBrowser()) return;
  const domain = window.location.hostname;
  const domains = [domain, "." + domain, "." + domain.split(".").slice(-2).join(".")];
  for (const p of paths) {
    for (const d of domains) {
      document.cookie = `${name}=; Max-Age=0; Path=${p}; Domain=${d}`;
    }
    document.cookie = `${name}=; Max-Age=0; Path=${p}`;
  }
}

export function loadConsent(): ConsentState | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== CONSENT_VERSION) return null;
    return {
      version: parsed.version,
      decidedAt: parsed.decidedAt,
      categories: { ...DEFAULT_CATEGORIES, ...parsed.categories },
    };
  } catch {
    return null;
  }
}

export function saveConsent(categories: ConsentCategories): ConsentState {
  const state: ConsentState = {
    version: CONSENT_VERSION,
    decidedAt: new Date().toISOString(),
    categories: { ...categories, essential: true },
  };
  if (isBrowser()) {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* ignore */
    }
    setCookie(CONSENT_COOKIE_NAME, JSON.stringify(state.categories), CONSENT_MAX_AGE_DAYS);
  }
  return state;
}

/** Cookies bekannter Tracking-Dienste entfernen, wenn Consent widerrufen wird. */
export function purgeTrackingCookies(scopes: { statistics?: boolean; marketing?: boolean }) {
  if (!isBrowser()) return;
  const cookies = document.cookie.split(";").map((c) => c.trim().split("=")[0]).filter(Boolean);

  const matchers: RegExp[] = [];
  if (scopes.statistics) {
    matchers.push(/^_ga/, /^_gid$/, /^_gat/, /^mf_/, /^_mf/);
  }
  if (scopes.marketing) {
    matchers.push(/^_gcl/, /^_gac/, /^_gads?$/, /^IDE$/, /^test_cookie$/);
  }

  for (const name of cookies) {
    if (matchers.some((re) => re.test(name))) {
      deleteCookie(name);
    }
  }
}
